import {
  Center,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useControllableState,
} from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'assets/icons/close_circle.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { RefObject, useLayoutEffect, useRef } from 'react';

interface SearchInputProps {
  value?: string;
  setValue?: (value: string) => void;
  onSearch: (value: string) => void;
  clearSearch: () => void;
  label: string;
  clearButtonLabel: string;
  defaultValue?: string;
  searchInputRef?: RefObject<HTMLInputElement>;
  focusOnMenuOpen?: boolean;
  isVisible?: boolean;
}

export const SearchInput = ({
  value: valueProp,
  setValue: setValueProp,
  onSearch,
  clearSearch,
  label,
  clearButtonLabel,
  defaultValue = '',
  focusOnMenuOpen = false,
  isVisible,
}: SearchInputProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useControllableState({
    defaultValue,
    value: valueProp,
    onChange: setValueProp,
  });
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

  const handleSearch = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (value && value.trim()) {
      onSearch(value);
    }
  };

  const onClearSearch = () => {
    setValue('');
    clearSearch();
  };

  useLayoutEffect(() => {
    if (focusOnMenuOpen && isVisible) {
      searchInputRef.current?.focus();
    }
  }, [focusOnMenuOpen, isVisible]);

  return (
    <InputGroup onSubmit={handleSearch} as='form' width={304}>
      <InputLeftElement h={'full'}>
        <IconButton
          type='submit'
          size='xs'
          borderRadius='full'
          variant='unstyled'
          onClick={handleSearch}
          aria-label={label}
          icon={
            <Center>
              <SearchIcon stroke='var(--vg-colors-upgrade-blue-500' />
            </Center>
          }
          data-testid='search-icons-button'
        />
      </InputLeftElement>
      <Input
        w={'100%'}
        value={value}
        onChange={onChange}
        placeholder={label}
        aria-label={label}
        borderColor={'gray.500'}
        borderRadius={'base'}
        ref={searchInputRef}
        role='combobox'
        aria-autocomplete='list'
        aria-haspopup='listbox'
        _placeholder={{ fontSize: 'sm' }}
      />
      {value && (
        <InputRightElement h={'full'}>
          <IconButton
            size='xs'
            borderRadius='full'
            variant='unstyled'
            onClick={onClearSearch}
            aria-label={clearButtonLabel}
            icon={
              <Center>
                <CloseIcon stroke='var(--vg-colors-outline-gray' />
              </Center>
            }
            data-testid='clear-icon-search-button'
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
};
